import type { Svg } from "@svgdotjs/svg.js";
import {
    HORIZONTAL_PADDING_PX,
    VERTICAL_PADDING_PX,
    COLUMN_GAP_PX,
    SECTION_STROKE_PX,
    DEFAULT_SHELF_HEIGHT_MM,
    WALL_STROKE_PX,
} from "../constant";
import type { SectionNomenclature } from "../types";
import { drawDimensionInMillimeters } from "./dimensions";
import { normalizeKey, normalizeToArray } from "../utils/math";
import { getRenderer, resolveItemType, fallbackRenderer } from "./items";
import type { ViewName } from "./items/types";

export const drawSideShelves = (
    svg: Svg,
    pixelsPerMillimeter: number,
    wallWidthMm: number,
    wallHeightMm: number,
    sections: (SectionNomenclature & { offsetCeiling: number })[],
    projectedFrom:
        | {
              front?: (SectionNomenclature & { offsetCeiling: number })[];
              back?: (SectionNomenclature & { offsetCeiling: number })[];
          }
        | undefined,
    view: ViewName,
    legendIndexByName?: Map<string, number>
) => {
    const originX = HORIZONTAL_PADDING_PX;
    const originY = VERTICAL_PADDING_PX;

    svg.rect(wallWidthMm * pixelsPerMillimeter, wallHeightMm * pixelsPerMillimeter)
        .move(originX, originY)
        .fill("none")
        .stroke({ width: WALL_STROKE_PX, color: "#535353" });

    svg.line(
        originX,
        originY + wallHeightMm * pixelsPerMillimeter,
        originX + wallWidthMm * pixelsPerMillimeter,
        originY + wallHeightMm * pixelsPerMillimeter
    ).stroke({ width: WALL_STROKE_PX, color: "#535353" });

    {
        const originXmm = originX / pixelsPerMillimeter;
        const originYmm = originY / pixelsPerMillimeter;
        const offsetMmOut = 12 / pixelsPerMillimeter;

        drawDimensionInMillimeters(
            svg,
            originXmm,
            originYmm + wallHeightMm,
            originXmm + wallWidthMm,
            originYmm + wallHeightMm,
            -offsetMmOut,
            `${wallWidthMm}`,
            pixelsPerMillimeter
        );
    }

    const dimensionMarkers: Array<{
        x1: number;
        y1: number;
        y2: number;
        x2: number;
        offset: number;
        value: string;
    }> = [];
    const seenHeightsBySide: Record<string, number[]> = {};

    const geometryBySection = sections.map((section, sectionIndex) => {
        const sectionWidthMm = section.width;
        const sectionHeightMm = section.height;
        const sectionTopOffsetMm = section.offsetCeiling;
        const xLeftPx = originX + (section.offset + sectionIndex * COLUMN_GAP_PX) * pixelsPerMillimeter;
        const yTopPx = originY + sectionTopOffsetMm * pixelsPerMillimeter;
        return {
            section,
            sectionWidthMm,
            leftRackHeightMm: section.leftRackHeight ?? sectionHeightMm,
            sectionHeightMm,
            sectionTopOffsetMm,
            xLeftPx,
            yTopPx,
        };
    });

    geometryBySection.forEach((geom, sectionIndex) => {
        if (geom.section.isIndent) return;

        const sectionWidthPx = geom.sectionWidthMm * pixelsPerMillimeter;
        const leftRackHeightPx = geom.leftRackHeightMm * pixelsPerMillimeter;

        const xLeftPx = geom.xLeftPx;
        const xRightPx = geom.xLeftPx + sectionWidthPx;
        const yTopPx = geom.yTopPx;
        const yLeftBottomPx = geom.yTopPx + leftRackHeightPx;
        const yBottomPx = geom.yTopPx + geom.sectionHeightMm * pixelsPerMillimeter;

        const polylinePoints = (
            [
                [xLeftPx, yLeftBottomPx],
                [xLeftPx, yTopPx],
                [xRightPx, yTopPx],
                geometryBySection?.[sectionIndex + 1] && !geometryBySection?.[sectionIndex + 1]?.section?.isIndent
                    ? []
                    : [xRightPx, yBottomPx],
            ] as number[][]
        ).flat() as number[];

        drawDimensionInMillimeters(
            svg,
            xLeftPx / pixelsPerMillimeter,
            yTopPx / pixelsPerMillimeter,
            xRightPx / pixelsPerMillimeter,
            yTopPx / pixelsPerMillimeter,
            4 / pixelsPerMillimeter,
            `${geom.sectionWidthMm}`,
            pixelsPerMillimeter
        );

        if (sectionIndex === 0) {
            dimensionMarkers.push({
                x1: xLeftPx / pixelsPerMillimeter,
                y1: originY / pixelsPerMillimeter + wallHeightMm,
                x2: xLeftPx / pixelsPerMillimeter,
                y2: yTopPx / pixelsPerMillimeter,
                offset: geom.section.offset,
                value: `${Math.round(wallHeightMm - geom.section.offsetCeiling)}`,
            });

            dimensionMarkers.push({
                x1: xLeftPx / pixelsPerMillimeter,
                y1: originY / pixelsPerMillimeter + wallHeightMm,
                x2: xLeftPx / pixelsPerMillimeter,
                y2: originY / pixelsPerMillimeter,
                offset: geom.section.offset,
                value: `${wallHeightMm}`,
            });
        }

        svg.polyline(polylinePoints)
            .fill("none")
            .stroke({ width: SECTION_STROKE_PX, color: "#a64022", linecap: "square", linejoin: "miter" });
    });

    const spansBySection: Record<number, Array<{ topMm: number; bottomMm: number }>> = {};

    geometryBySection.forEach((geom, secIdx) => {
        spansBySection[secIdx] = [];

        normalizeToArray(geom.section.items).forEach((shelfItem) => {
            const bottomFromFloorMm = (Number(shelfItem?.position?.[1]) || 0) * 1000;
            const heightRawMm = Number(shelfItem?.height) || 0;
            const shelfHeightMm = heightRawMm > 0 ? heightRawMm : DEFAULT_SHELF_HEIGHT_MM;
            const bottomFromCeilingMm = wallHeightMm - bottomFromFloorMm;
            const topFromCeilingItemMm = bottomFromCeilingMm - shelfHeightMm;

            const visibleTopMm = Math.max(topFromCeilingItemMm, geom.sectionTopOffsetMm);
            const visibleBottomMm = Math.min(bottomFromCeilingMm, geom.sectionTopOffsetMm + geom.sectionHeightMm);
            const visibleHeightMm = Math.max(0, visibleBottomMm - visibleTopMm);
            if (visibleHeightMm <= 0) return;

            const shelfWidthMm = Number(shelfItem?.width) || 0;
            const shelfDepthMm = Number(shelfItem?.depth) || 0;
            const yTopVisiblePx = VERTICAL_PADDING_PX + visibleTopMm * pixelsPerMillimeter;

            const uniqueHeightFromFloor = Math.round(wallHeightMm - topFromCeilingItemMm);
            if (!seenHeightsBySide?.[geom.section.side]?.includes(uniqueHeightFromFloor)) {
                dimensionMarkers.push({
                    x1: geom.xLeftPx / pixelsPerMillimeter,
                    y1: VERTICAL_PADDING_PX / pixelsPerMillimeter + wallHeightMm,
                    x2: geom.xLeftPx / pixelsPerMillimeter,
                    y2: yTopVisiblePx / pixelsPerMillimeter,
                    offset: geom.section.offset,
                    value: `${uniqueHeightFromFloor}`,
                });
                seenHeightsBySide[geom.section.side] = [
                    ...(seenHeightsBySide[geom.section.side] ?? []),
                    uniqueHeightFromFloor,
                ];
            }

            const bboxPx = {
                x: geom.xLeftPx,
                y: yTopVisiblePx,
                w: shelfWidthMm * pixelsPerMillimeter,
                h: visibleHeightMm * pixelsPerMillimeter,
            };
            const key = resolveItemType(shelfItem);
            const renderer = getRenderer(key) ?? fallbackRenderer;
            renderer({
                svg,
                ppm: pixelsPerMillimeter,
                view,
                bboxPx,
                item: shelfItem,
                section: geom.section,
            });

            if (shelfDepthMm) {
                const text = svg
                    .text(String(shelfDepthMm))
                    .font({ size: 128 * pixelsPerMillimeter })
                    .move(geom.xLeftPx + (shelfWidthMm * pixelsPerMillimeter) / 2, yTopVisiblePx)
                    .fill("#a64022");
                text.cx(geom.xLeftPx + (shelfWidthMm * pixelsPerMillimeter) / 2);
                text.cy(yTopVisiblePx - (128 * pixelsPerMillimeter) / 2);
            }

            const nameKey = (shelfItem?.name ?? "").replace(/\s+/g, " ").toLowerCase().trim();
            const idxLabel = legendIndexByName?.get(nameKey);
            if (idxLabel) {
                const sectionLeftPx = geom.xLeftPx;
                const sectionRightPx = geom.xLeftPx + geom.sectionWidthMm * pixelsPerMillimeter;

                const circleStroke = 0.8;
                const padX = 3;
                const padY = 3;

                const r = Math.max(5, Math.min(10, 2.5 * pixelsPerMillimeter));

                let cx = bboxPx.x + bboxPx.w - padX - r - circleStroke / 2;
                const cy = bboxPx.y - padY - r - circleStroke / 2;

                if (cx + r + circleStroke / 2 > sectionRightPx) {
                    cx = sectionRightPx - r - circleStroke / 2 - 1;
                }
                if (cx - r - circleStroke / 2 < sectionLeftPx) {
                    cx = sectionLeftPx + r + circleStroke / 2 + 1;
                }

                svg.circle(r * 2)
                    .center(cx, cy)
                    .fill("#fff")
                    .stroke({ width: circleStroke, color: "#535353" });

                const fontPx = Math.max(6, Math.min(10, r * 0.95));
                svg.text(String(idxLabel))
                    .font({ size: fontPx, family: "system-ui", weight: "600" })
                    .fill("#535353")
                    .attr({ "text-anchor": "middle", "dominant-baseline": "middle" })
                    .center(cx, cy);
            }

            spansBySection[secIdx].push({ topMm: visibleTopMm, bottomMm: visibleBottomMm });
        });
    });

    Object.entries(spansBySection).forEach(([idxStr, spans]) => {
        const secIdx = Number(idxStr);
        if (!spans.length) return;

        const sorted = spans.slice().sort((a, b) => a.topMm - b.topMm);
        const merged: Array<{ topMm: number; bottomMm: number }> = [];
        for (const s of sorted) {
            if (!merged.length || s.topMm > merged[merged.length - 1].bottomMm) {
                merged.push({ ...s });
            } else {
                merged[merged.length - 1].bottomMm = Math.max(merged[merged.length - 1].bottomMm, s.bottomMm);
            }
        }

        for (let i = 0; i < merged.length - 1; i++) {
            const gapTopMm = merged[i].bottomMm;
            const gapBottomMm = merged[i + 1].topMm;
            const gapMm = gapBottomMm - gapTopMm;
            if (gapMm <= 0.5) continue;

            const gapTopPx = VERTICAL_PADDING_PX + gapTopMm * pixelsPerMillimeter;
            const gapBottomPx = VERTICAL_PADDING_PX + gapBottomMm * pixelsPerMillimeter;
            const gapMidPx = (gapTopPx + gapBottomPx) / 2;
            const gapHpPx = gapBottomPx - gapTopPx;

            if (gapHpPx >= 4) {
                const geom = geometryBySection[secIdx];

                const fontPx = 64 * pixelsPerMillimeter;
                const label = String(Math.round(gapMm));

                const t = svg
                    .text(label)
                    .font({ size: fontPx, family: "system-ui" })
                    .fill("#777")
                    .attr({ "text-anchor": "middle", "dominant-baseline": "middle" });

                t.center(geom.xLeftPx + fontPx / 2, gapMidPx);
                t.rotate(90, geom.xLeftPx + fontPx / 2, gapMidPx);
            }
        }
    });

    const laneStepPx = 12;
    const basePadPx = 0;
    const laneStepMm = laneStepPx / pixelsPerMillimeter;
    const basePadMm = basePadPx / pixelsPerMillimeter;
    const halfCount = Math.ceil(dimensionMarkers.length / 2);

    [...dimensionMarkers]
        .sort((a, b) => Number(a.value) - Number(b.value))
        .forEach((marker, markerIndex) => {
            const isLeftHalf = markerIndex < halfCount;
            const laneIdx = isLeftHalf ? markerIndex + 1 : markerIndex + 1 - halfCount;

            const offset = isLeftHalf
                ? marker.offset + basePadMm + laneStepMm * laneIdx
                : -(wallWidthMm - marker.offset + basePadMm + laneStepMm * laneIdx);

            drawDimensionInMillimeters(
                svg,
                marker.x1,
                marker.y1,
                marker.x2,
                marker.y2,
                offset,
                marker.value,
                pixelsPerMillimeter
            );
        });

    const drawProjectedGroup = (
        sourceSections: (SectionNomenclature & { offsetCeiling: number })[] | undefined,
        which: "front" | "back"
    ) => {
        if (!sourceSections?.length) return;
        const viewSide: "left" | "right" =
            sections.find((s) => s.side === "right" || s.side === "left")?.side === "right" ? "right" : "left";

        sourceSections.forEach((sourceSection) => {
            const sectionTopMm = sourceSection.offsetCeiling;
            const sectionBottomMm = sourceSection.offsetCeiling + sourceSection.height;

            normalizeToArray(sourceSection.items).forEach((shelfItem) => {
                const bottomFromFloorMm = (Number(shelfItem?.position?.[1]) || 0) * 1000;
                const heightRawMm = Number(shelfItem?.height) || 0;
                const shelfHeightMm = heightRawMm > 0 ? heightRawMm : DEFAULT_SHELF_HEIGHT_MM;
                const depthMm = Math.max(Number(shelfItem?.depth) || 0, 0);
                if (depthMm <= 0) return;

                const bottomFromCeilingMm = wallHeightMm - bottomFromFloorMm;
                const topFromCeilingItemMm = bottomFromCeilingMm - shelfHeightMm;
                const visibleTopMm = Math.max(topFromCeilingItemMm, sectionTopMm);
                const visibleBottomMm = Math.min(bottomFromCeilingMm, sectionBottomMm);
                const visibleHeightMm = Math.max(0, visibleBottomMm - visibleTopMm);
                if (visibleHeightMm <= 0) return;

                const yTopVisiblePx = VERTICAL_PADDING_PX + visibleTopMm * pixelsPerMillimeter;

                const frontXpx = (depth: number) =>
                    viewSide === "left"
                        ? HORIZONTAL_PADDING_PX + (wallWidthMm - depth) * pixelsPerMillimeter
                        : HORIZONTAL_PADDING_PX;
                const backXpx = (depth: number) =>
                    viewSide === "left"
                        ? HORIZONTAL_PADDING_PX
                        : HORIZONTAL_PADDING_PX + (wallWidthMm - depth) * pixelsPerMillimeter;

                const xLeftPx = (which === "front" ? frontXpx : backXpx)(depthMm);

                svg.rect(depthMm * pixelsPerMillimeter, visibleHeightMm * pixelsPerMillimeter)
                    .move(xLeftPx, yTopVisiblePx)
                    .fill("none")
                    .stroke({ width: SECTION_STROKE_PX, color: "#a64022" });
            });
        });
    };

    drawProjectedGroup(projectedFrom?.front, "front");
    drawProjectedGroup(projectedFrom?.back, "back");
};
